import { BehaviorSubject } from 'rxjs';
import { CricketMatchModel, MatchModelUnion, SportMatchModel } from '@nf/types/odds';
import { StreamerSetting, UserInfo } from '@nf/types/account';
import { pingoalSetting } from '@/settings/pingoal-settings';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { StreamingType } from '@/store/streaming';
import { getMatchIdFromProviderList } from '@/utils/match-util';
import { isSabaSport } from '@/utils/saba-sports-util';

const streamingClosingCountdownSubject = new BehaviorSubject({
	isCountdownStarted: false,
	shouldCloseStreaming: false
});

const virtualLeagueGroupId = [42, 74, 108, 113, 122, 151];
const LiveStreamerSrc = [20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];
const EsportStreamingSrc = ['14', '16'];
const GVEnum =
{
	'BetRadar': 1,     // 0001
	'GameVisualization': 2,   // 0010
	'LSport': 4,   // 0100
}
export interface ChannelModel {
	label: string;
	value: string;
	sid: string;
	fixing: boolean;
	siteId: number;
	type: StreamingType;
}

export interface EsportChannelModel {
	value: string;
	url: string;
	lang: number;
	label: string;
	sourceType: number;
}
export type VideoDeviceType = 'desktop' | 'mobile';
export interface Mslt {
	src: string;
	sid: string;
	fixing: boolean;
	smsc?: number;
	siteId?: number;
}

const getVideoOptions = (match: MatchModelUnion, isOddsPage: boolean, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null) => {
	const isDisableOptions = (sportId: number, leagueGroupId: number, leagueId: number, siteConfig: SiteConfigsSettingsData) => {
		const sportIds = siteConfig?.FilterStreamingSportList.split(',').map(Number);
		const leagueIds = siteConfig?.FilterStreamingLeagueList.split(',').map(Number);
		const map = siteConfig?.VirtualStreamingLeagueGroupMap;
		const virtualSportIds = Object.keys(map).map(Number).filter(key => key !== 0);
		const virtualLeagueGroupIds = [...new Set(Object.values(map).flat().map(Number))];

		const checkLeagueGroup = (disableSportId: number) => {
			const isCrossSportLeagueGroupId = leagueGroupId === 42;
			const isLeagueGroupMatchSportId = disableSportId === 993 && sportId === 2 || disableSportId === 997 && sportId === 1; // 因 993 & 997 的 42 會互相影響, 目前無規則可循故 hardcode 轉換
			return (!isCrossSportLeagueGroupId || isCrossSportLeagueGroupId && isLeagueGroupMatchSportId) && map[disableSportId].includes(leagueGroupId.toString());
		}

		if (sportIds.includes(sportId) && !virtualLeagueGroupIds.includes(leagueGroupId)) { // 篩選一般 sport (但需跳過虛擬賽事)
			return true;
		}

		if (sportIds.some(id => virtualSportIds.includes(id) && checkLeagueGroup(id))) { // 篩選虛擬賽事
			return true;
		}

		if (leagueIds.includes(leagueId)) { // 篩選 league
			return true;
		}

		return false;
	}

	const hasVideo = (match: MatchModelUnion) => {
		if (siteconfigsData && (!siteconfigsData?.EnableStreaming || isDisableOptions(match.GameID, match.LeagueGroupId, match.LeagueId, siteconfigsData))) {
			return false;
		}
		const marketValue = match?.MaT;
		if (marketValue === 'l' || isOddsPage) {
			if (match.GameID == 161 || match.GameID == 164) {
				return true;
			}
			if (getVideoInfo(match, siteconfigsData, accountInfo, '')) {
				return true;
			}
		}
		return false;
	};

	const hasGV = (match: MatchModelUnion) => {
		if (match.GvCombo !== 0 && match.GvCombo !== GVEnum.GameVisualization) {
			const flags = match.GvCombo;
			if (flags & GVEnum.BetRadar) {
				// Bit zero is set.
			}
			if (flags & GVEnum.GameVisualization) {
				// Bit one is set.
			}
			if (flags & GVEnum.LSport) {
				// Bit two is set.
				return true
			}
			if ((typeof accountInfo?.ActStatus != 'undefined' && accountInfo?.ActStatus == 2) &&
				//TODO (window.siteSetting.OpenGV && window._ShowGV && match.GV)) {
				(match.GV || match?.lsmid)) {
				return true;
			}
		}
		return false;
	}

	const hasDecimalWidgets = (match: CricketMatchModel) => {
		return siteconfigsData?.CanSeeDecimalWidgets && !!match.dwmid;
	}

	const hasLSports = (match: MatchModelUnion) => {
		return hasGV(match) && match.lsmid;
	}

	const hasStreamer = (match: MatchModelUnion) => {
		if (siteconfigsData && isDisableOptions(match.GameID, match.LeagueGroupId, match.LeagueId, siteconfigsData)) {
			return 0;
		}
		const { sourceInfoList, hasStreamer } = getVideoInfoList(match, siteconfigsData, accountInfo);
		if (sourceInfoList.length > 0 && hasStreamer) {
			let realSrc = 0, srcIdx = 0;
			if (sourceInfoList[0].src && isNaN(sourceInfoList[0].src as any)) {
				if ((sourceInfoList[0].src).split('-').length > 1) {
					realSrc = Number((sourceInfoList[0].src).split('-')[0]);
					srcIdx = Number((sourceInfoList[0].src).split('-')[1]);
				}
			} else {
				realSrc = Number(sourceInfoList[0].src);
			}
			return ((LiveStreamerSrc.indexOf(realSrc)) > -1) ? realSrc : 0;
		} else {
			return 0;
		}
	}

	const hasSportsStatistic = (match: MatchModelUnion) => {
		return match && siteconfigsData?.EnableStaticInfo && !!getMatchIdFromProviderList(match, '201') && match.GameID !== 43;
	}

	const hasEsportSportsStatistic = (match: MatchModelUnion) => {
		return match && siteconfigsData?.EnableOpenStatisticInfo && match.hasPM && match.GameID === 43;
	}


	const hasChatroom = (match: MatchModelUnion) => {
		return siteconfigsData?.EnableChatroom && accountInfo?.ActStatus !== 0 && hasVideo(match) && hasStreamer(match);
	}
	return {
		hasVideo: false || (accountInfo?.ActStatus !== 0 && hasVideo(match)),
		hasGV: false || (accountInfo?.ActStatus !== 0 && hasGV(match as CricketMatchModel)),
		hasLSports: false || ([6, 9, 50].indexOf(match?.GameID) > -1 && hasLSports(match as CricketMatchModel)),
		hasDecimalWidgets: false || (match?.GameID == 50 && hasDecimalWidgets(match as CricketMatchModel)),
		hasStreamer: 0 || hasStreamer(match),
		hasSportsStatistic: false || hasSportsStatistic(match),
		hasChatroom: false || hasChatroom(match),
		hasEsportSportsStatistic: false || hasEsportSportsStatistic(match),
	}
}

const checkPinGoalLeague = (match: MatchModelUnion) => {
	const { LEAGUE_GROUP_ID_LIST } = pingoalSetting;
	return LEAGUE_GROUP_ID_LIST.indexOf(match.LeagueGroupId)
}


const getChannelList = (resourceList: string[], match: MatchModelUnion, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null) => {
	const { sourceInfoList } = getVideoInfoList(match, siteconfigsData, accountInfo);
	let i = 1; // For Source
	let j = 1;
	const jMap = Array<number>(); // For Streamer
	const channelList = Array<ChannelModel>();
	sourceInfoList.forEach((item, index) => {
		const sid = item.sid;
		let realSrc, srcIdx = '';
		if (item.src && isNaN(item.src as any)) {
			if ((item.src).split('-').length > 1) {
				realSrc = (item.src).split('-')[0];
				srcIdx = (item.src).split('-')[1];
			}
		} else {
			realSrc = item.src;
		}

		let name;
		let type: StreamingType;
		if (realSrc === '20') {
			if (!jMap[realSrc]) jMap[realSrc] = j++;
			name = `${resourceList[0]} ${index + 1}`;
			type = 'Streamer';
		} else if (realSrc === '21' || realSrc === '22' || realSrc === '23' || realSrc === '29') {
			if (!jMap[realSrc]) jMap[realSrc] = j++;
			name = `${resourceList[0]} ${index + 1}`; // 名稱測試辨認用
			type = 'Streamer';
		} else if (realSrc === '24' || realSrc === '25' || realSrc === '26' || realSrc === '27' || realSrc === '28') {
			if (!jMap[realSrc]) jMap[realSrc] = j++;
			// 名稱測試好辨認用，之後改回流水號編排
			name = `${resourceList[0]} ${index + 1}`;
			type = 'Streamer';
		} else {
			name = `${resourceList[1].replace('{0}', (i++).toString())}`;
			type = 'Streaming';
		}

		channelList.push({ label: name, value: item.src, sid: sid, fixing: item.fixing, type, siteId: item.siteId || 0 });
	})
	// console.log('channelList', channelList);
	return channelList;
}

const getStreamingAllowSiteIdList = (siteconfigsData?: SiteConfigsSettingsData | null) => {
	let streamingSiteIds = [];
	const licenseeSiteId = parseInt(siteconfigsData?.GetMessageID || '0');
	streamingSiteIds = [0, -2, licenseeSiteId];
	return streamingSiteIds;
}

const getVideoInfoList = (match: MatchModelUnion, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null) => {
	if (!match) {
		return {
			sourceInfoList: [],
			hasVideo: false,
			hasStreamer: 0,
		};
	}
	const videoSrc = siteconfigsData?.StreamingSrc ? siteconfigsData?.StreamingSrc : [];
	let infoList = Array<Mslt>();
	const hasStreamer = Array<Mslt>();
	let sourceList = Array<Mslt>();
	const allowedStreamingSiteIds = getStreamingAllowSiteIdList(siteconfigsData);
	const mslt = match?.mslt || {};
	const msltArr = Object.keys(mslt);
	const isMslt = mslt && msltArr.length > 0;

	if (isMslt) {
		msltArr.forEach((key) => {
			const obj = mslt[key];
			const hasExclusive = obj['SiteId'] > 0;
			const isAllowedSiteId = allowedStreamingSiteIds.includes(obj['SiteId']); // 0, -2, licenseeSiteId ，mslt 都是0 or licenseeSiteId
			const isSpecificStreamingSrc = [21, 22, 23, 29].includes(obj['smsc']);
			const isMcdnStreamerSrc = [24, 25, 26, 27, 28].includes(obj['smsc']);
			const othersStreamerSrc = [20].includes(obj['smsc']);
			const pluginStreamerSrc = [30].includes(obj['smsc']);

			if (isAllowedSiteId) {
				if (isSpecificStreamingSrc) {
					switch (siteconfigsData?.ExclusiveStreamerType || '0') {
						case '1': // 1: only show exclusive streamer
							if (obj['SiteId'] === parseInt(siteconfigsData?.GetMessageID || '0')) {
								infoList.push({
									src: obj['smsc'] + '-1',
									sid: obj['smid'],
									fixing: obj['smfx'],
									smsc: obj['smsc'],
									siteId: obj['SiteId']
								});
								infoList.push({
									src: obj['smsc'] + '-2',
									sid: obj['smid'],
									fixing: obj['smfx'],
									smsc: obj['smsc'],
									siteId: obj['SiteId']
								});
							}
							break;
						case '2': // 2: if has exclusive streamer only show exclusive streamer else show non-exclusive
							infoList.push({
								src: obj['smsc'] + '-1',
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							infoList.push({
								src: obj['smsc'] + '-2',
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							break;
						case '0': // show all streamer
						default:
							infoList.push({
								src: obj['smsc'] + '-1' + (hasExclusive ? '-ex' : ''),
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							infoList.push({
								src: obj['smsc'] + '-2' + (hasExclusive ? '-ex' : ''),
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							break;
					}
				} else if (isMcdnStreamerSrc || othersStreamerSrc) {
					switch (siteconfigsData?.ExclusiveStreamerType || '0') {
						case '1':
							if (obj['SiteId'] === parseInt(siteconfigsData?.GetMessageID || '0')) {
								infoList.push({
									src: obj['smsc'] + '-1',
									sid: obj['smid'],
									fixing: obj['smfx'],
									smsc: obj['smsc'],
									siteId: obj['SiteId']
								});
							}
							break;
						case '2':
							infoList.push({
								src: obj['smsc'] + '-1',
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							break;
						case '0':
						default:
							infoList.push({
								src: obj['smsc'] + '-1' + (hasExclusive ? '-ex' : ''),
								sid: obj['smid'],
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							break;
					}
				} else if (pluginStreamerSrc) {

					switch (siteconfigsData?.ExclusiveStreamerType || '0') {

						case '1':
							if (obj['SiteId'] === parseInt(siteconfigsData?.GetMessageID || '0')) {
								infoList.push({
									src: obj['smsc'] + '',
									sid: obj['smid'] + `${'30'}`,
									fixing: obj['smfx'],
									smsc: obj['smsc'],
									siteId: obj['SiteId']
								});
							}
							break;
						case '2':
						case '0':
						default:
							infoList.push({
								src: obj['smsc'] + '',
								sid: obj['smid'] + `${'30'}`,
								fixing: obj['smfx'],
								smsc: obj['smsc'],
								siteId: obj['SiteId']
							});
							break;
					}
				} else {
					infoList.push({
						src: obj['smsc'] + '',
						sid: obj['smid'],
						fixing: obj['smfx'],
						smsc: obj['smsc'],
						siteId: obj['SiteId']
					});
				}
				
				infoList.sort((a, b) => {
					if (a.siteId !== b.siteId) {
						return a.siteId !== 0 ? -1 : 1;
					} else {
						return (a.smsc || 0) - (b.smsc || 0);
					}
				});
			} else if (!isAllowedSiteId && !obj.hasOwnProperty('SiteId')) {
				const obj = mslt[key];
				if (!!~[21, 22, 23, 29].indexOf(obj['smsc'])) {
					infoList.push({
						src: obj['smsc'] + '-1',
						sid: obj['smid'],
						fixing: obj['smfx']
					});
					infoList.push({
						src: obj['smsc'] + '-2',
						sid: obj['smid'],
						fixing: obj['smfx']
					});
				} else {
					infoList.push({
						src: obj['smsc'].toString(),
						sid: obj['smid'],
						fixing: obj['smfx']
					});
				}
			}
		})
		if (siteconfigsData?.ExclusiveStreamerType === '2') {
			if (infoList.findIndex((element) => element.siteId === parseInt(siteconfigsData?.GetMessageID || '0')) >= 0) {
				infoList = infoList.filter(item => item?.siteId === parseInt(siteconfigsData?.GetMessageID || '0') || item.src === '19');
			} else {
				infoList = infoList.filter(item => item?.siteId === 0);
			}
		}
	}
	if (match.Src) {
		infoList.push({
			src: match.Src + '',
			sid: match.Sid,
			fixing: match.Fixing1
		});
	}

	if (match.Src2) {
		infoList.push({
			src: match.Src2 + '',
			sid: match.Sid2 ?? '',
			fixing: match.Fixing2
		});
	}
	//streaming list
	for (let i = 0; i < infoList.length; i++) {
		let srcId: number = 0;
		if (infoList[i].src && infoList[i].src.indexOf('-') > -1) {
			if ((infoList[i].src).split('-').length > 1) {
				srcId = Number((infoList[i].src).split('-')[0]);
			}
		} else {
			srcId = Number(infoList[i].src);
		}

		const sid = infoList[i].sid;
		if (!sid) continue; // sid not included null, '', 0
		switch (srcId) {
			case 13:	//in house
				sourceList.push(infoList[i]);
				if ((sid != '' && parseInt(sid) >= 9975 && parseInt(sid) <= 9999 && !checkPinGoalLeague(match))) {
					sourceList.push({
						src: 11 + '',
						sid: sid,
						fixing: infoList[i].fixing
					});
				}
				break;
			case 14:	//esport streaming
				sourceList.push(infoList[i]);
				break;
			case 15:	//betgenius
				sourceList.push(infoList[i]);
				break;
			case 16:	//twitch streaming
				sourceList.push(infoList[i]);
				// if (accountInfo?.CanSeeESTwitchStreaming) {
				// 	sourceList.push(infoList[i]);
				// }
				break;
			case 19:	//cloud rock
				if (accountInfo?.CanSeeCloudRockStreaming) {
					sourceList.push(infoList[i]);
				}
				break;
			case 20:	//live streamer(CN)
				if (accountInfo?.LicenseeSiteSetting?.StreamerSetting?.CanSeeStreamer) {
					hasStreamer.push(infoList[i]);
				}
				break;
			case 21:	//live streamer(VN)
			case 22:	//live streamer(TH)
			case 23:	//live streamer(ID)
			case 24:
			case 25:
			case 26:
			case 27:
			case 28:
			case 29:
				const streamingKey = `CanSeeStreamer${srcId}`;
				if (accountInfo?.LicenseeSiteSetting?.StreamerSetting[streamingKey as keyof StreamerSetting]) {
					hasStreamer.push(infoList[i]);
				}
				break;
			case 11:	//glive
				if ((parseInt(infoList[i].sid) >= 9975 && parseInt(infoList[i].sid) <= 9999)) {
					sourceList.push(infoList[i]);
				} else if (siteconfigsData?.CanSeeGLiverStreaming || videoSrc.includes(srcId)) {
					sourceList.push(infoList[i]);
				}
				break;
			case 18:	//weverdat, kabaddi
				if ((siteconfigsData?.WeverdatStreamingUrl && match.GameID == 50) || (siteconfigsData?.KabaddiVStreamingUrl && match.GameID == 47)) {
					sourceList.push(infoList[i]);
				}
				break;
			case 30:
				if (siteconfigsData?.EnableStreamingPlugin && accountInfo?.LicenseeSiteSetting?.EnablePluginStreamerSources?.includes(30)) {
					hasStreamer.push(infoList[i]);
				}
				break;
			default:
				if (videoSrc.includes(srcId) && /^[0-9]+$/.test(infoList[i].sid)) {
					sourceList.push(infoList[i]);
				}
				break;
		}
	}

	if (sourceList.length > 2) sourceList = sourceList.slice(0, 2); //目前只提供2個頻道
	if (hasStreamer.length > 0) { // 主播放前面
		return {
			hasVideo: sourceList.length > 0,
			hasStreamer: hasStreamer.length > 0,
			sourceInfoList: hasStreamer.concat(sourceList)
		}
	} else {
		return {
			hasVideo: sourceList.length > 0,
			hasStreamer: false,
			sourceInfoList: sourceList,
		};
	}
}

const getVideoInfo = (match: MatchModelUnion, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null, key?: string) => {
	if (match) {
		const { sourceInfoList, hasVideo, hasStreamer } = getVideoInfoList(match, siteconfigsData, accountInfo);

		if (sourceInfoList.length == 0 || (!hasVideo && !hasStreamer)) return null;
		if (!key && hasVideo) {
			return sourceInfoList[0];
		} else {
			return sourceInfoList.find(item => (item['src'] == key) || (item['src'] == (key + '-1')));
		}
	}
}

const setClosingCountdownStart = (shouldStart: boolean) => {
	streamingClosingCountdownSubject.next({
		...streamingClosingCountdownSubject.value,
		isCountdownStarted: shouldStart,
	});
};

const closeEndedStreaming = () => {
	streamingClosingCountdownSubject.next({
		isCountdownStarted: false,
		shouldCloseStreaming: true
	});
};

const resetClosingCountdown = () => {
	streamingClosingCountdownSubject.next({
		isCountdownStarted: false,
		shouldCloseStreaming: false
	});
};

const isStreamingPlugin = (match: SportMatchModel, sport: string | string[], siteconfigsData?: SiteConfigsSettingsData | null) => {
	return siteconfigsData?.EnableStreamingPlugin
		&& [1, 2].includes(match.GameID)
		&& !isSabaSport(Number(sport))
		&& !virtualLeagueGroupId.includes(match.LeagueGroupId);
}

const checkPluginType = (streamingType: StreamingType, enableChatroom: boolean, hasStreamer: number, isMiniStreaming?: boolean) => {

	if (isMiniStreaming) {
		return 1;
	}

	if (!streamingType) {
		return (enableChatroom && hasStreamer > 0) ? 3 : 0;
	}
	if (enableChatroom && hasStreamer > 0) {
		return 2;
	}
	return 1;
}

export const streamingService = {
	LiveStreamerSrc,
	EsportStreamingSrc,
	getVideoOptions,
	getChannelList,
	getVideoInfo,
	getVideoInfoList,
	streamingClosingCountdown$: streamingClosingCountdownSubject.asObservable(),
	setClosingCountdownStart,
	closeEndedStreaming,
	resetClosingCountdown,
	isStreamingPlugin,
	checkPluginType
};